import React, { useMemo } from 'react';
import { Box, Typography, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, Avatar, Button, TableSortLabel, styled, Grid2, IconButton, Tooltip, FormControlLabel, Switch } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SyncTwoToneIcon from '@mui/icons-material/SyncTwoTone';
import Asset360Modal from './Asset360Modal';
import RoleBasedAccess from '../../services/RoleBasedAccess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadFilesAsync } from '../../services/FileDownloader';
import { useAssetsContext } from '../../context/AssetsContext';
import { useAssetTableLogic } from '../../hooks/assets/useAssetTableLogic';
import AssetStatusTag from './AssetStatusTag';
import { Asset } from '../../state/types';
import { getMissingCameraPositions, getMissingCameraTooltip } from '../../utils/missingCameraPositions';
import { getDirectionalThumbnail } from '../../utils/assetImages';
import { useSession } from '../../context/SessionContext';

const truncateVinDisplay = (value?: string) => {
  if (!value) return '';
  return value.length > 17 ? `${value.slice(0, 17)}...` : value;
};

const AssetDesktopTable: React.FC = () => {
  const navigate = useNavigate();
  const { assets, sortDirection, setSortDirection, compactRows, setCompactRows } = useAssetsContext();
  const { systems } = useSession();
  const {
    openModal,
    modalAsset,
    thumbnailErrors,
    handleThumbnailError,
    handle360Click,
    handleCloseModal,
    handleCheckboxClick,
    handleSelectAllClick,
    buildAssetDetailPath,
    buildAssetNavState,
    isSelected,
    isAllSelected,
    isIndeterminate,
  } = useAssetTableLogic();

  const SpinningButton = styled(Button)(({ theme }) => ({
    borderRadius: '5px',
    padding: '12px',
    color: theme.palette.common.black,
    backgroundColor: theme.palette.warning.main,
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `0 2px 4px ${theme.palette.grey[500]}`,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  }));

  const configNameByKeyBySystem = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    for (const sys of systems ?? []) {
      if (!sys?.configurations) continue;
      map[sys.systemCode] = {};
      for (const [key, cfg] of Object.entries(sys.configurations as Record<string, any>)) {
        const name = cfg?.clientProcess?.name;
        if (typeof name === 'string') map[sys.systemCode][key] = name;
      }
    }
    return map;
  }, [systems]);

  const imageCountItems = [
    { key: 'imageExterior', label: 'Exterior', compactLabel: 'EXT', getCount: (asset: Asset) => asset.imageExterior?.length ?? 0 },
    { key: 'imageCrop', label: 'Cropped', compactLabel: 'CRP', getCount: (asset: Asset) => asset.imageCrop?.length ?? 0 },
    { key: 'imageFeatured', label: 'Featured', compactLabel: 'FEAT', getCount: (asset: Asset) => asset.imageFeatured?.length ?? 0 },
    { key: 'imageManual', label: 'Manual', compactLabel: 'MAN', getCount: (asset: Asset) => asset.imageManual?.length ?? 0 },
    { key: 'imagePano', label: 'Panoramic', compactLabel: 'PANO', getCount: (asset: Asset) => asset.imagePano?.length ?? 0 },
    { key: 'imageUnderCarriage', label: 'UC', compactLabel: 'UC', getCount: (asset: Asset) => asset.imageUndercarriage?.length ?? 0 },
    { key: 'imageBgr', label: 'BGR', compactLabel: 'BGR', getCount: (asset: Asset) => asset.imageBgr?.length ?? 0 },
  ];

  return (
    <>
      <TableContainer component={Paper}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                data-cy="asset-table-compact-toggle"
                size="small"
                checked={compactRows}
                onChange={(_, checked) => setCompactRows(checked)}
              />
            }
            label="Compact rows"
            sx={{ mr: 0 }}
          />
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <RoleBasedAccess allowedRoles={["Internal", "Client Plus"]}>
                <TableCell padding="checkbox">
                  <Checkbox
                    data-cy="asset-table-select-all"
                    onClick={handleSelectAllClick}
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: (theme) => theme.palette.status.accent,
                      },
                    }}
                  />
                </TableCell>
              </RoleBasedAccess>
              <TableCell>ID</TableCell>
              <TableCell>Image Counts</TableCell>
              <TableCell>Processing</TableCell>
              <TableCell>Syndication</TableCell>
              <TableCell>
                <TableSortLabel
                  data-cy="asset-table-sort-date"
                  active
                  direction={sortDirection}
                  onClick={() =>
                    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
                  }
                >
                  Post Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Config</TableCell>
              <TableCell>360</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset) => {
              const missingCameraPositions = getMissingCameraPositions(asset);
              const missingCameraTooltip = getMissingCameraTooltip(asset);
              const nonZeroImageCounts = imageCountItems
                .map(({ key, label, compactLabel, getCount }) => ({
                  key,
                  label,
                  compactLabel,
                  count: getCount(asset),
                }))
                .filter(({ count }) => count > 0);

              const thumbnailHeight = compactRows ? 60 : 120;
              const thumbnailWidth = compactRows ? 100 : 200;
              const fallbackHeight = compactRows ? 56 : 100;
              const fallbackWidth = compactRows ? 96 : 180;
              const actionColumnHeight = compactRows ? 76 : 140;
              const thumbnailSrc = getDirectionalThumbnail(asset, systems);

              return (
              <TableRow
                key={asset.assetInstanceId}
                hover
              >
                <RoleBasedAccess allowedRoles={["Internal", "Client Plus"]}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      data-cy={`asset-table-checkbox-${asset.assetInstanceId}`}
                      onClick={(e) => handleCheckboxClick(e, asset)}
                      checked={isSelected(asset)}
                      size="small"
                      sx={{
                        '&.Mui-checked': {
                          color: (theme) => theme.palette.status.accent,
                        },
                      }}
                    />
                  </TableCell>
                </RoleBasedAccess>
                <TableCell>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <Box display="flex" alignItems="center" flexDirection="column" justifyContent="center" gap={0.25}>
                      <Link
                        data-cy={`asset-table-link-${asset.assetInstanceId}`}
                        component={RouterLink}
                        to={buildAssetDetailPath(asset)}
                        state={buildAssetNavState(asset)}
                        underline="hover"
                        title={asset.assetId}
                        sx={{
                          color: "inherit",
                          fontWeight: 500,
                          textAlign: "center",
                          marginBottom: 0.5,
                          wordBreak: "break-word",
                          cursor: "pointer",
                          fontSize: "1rem",
                        }}
                      >
                        {truncateVinDisplay(asset.assetId)}
                      </Link>

                      {asset.archived ? (
                        <Box
                          sx={{
                            height: thumbnailHeight,
                            width: thumbnailWidth,
                            backgroundColor: '#ffffff',
                            border: '1px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.85,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.8rem',
                              color: 'black',
                              textAlign: 'center',
                            }}
                          >
                            Archived Asset
                          </Typography>
                        </Box>
                      ) : thumbnailSrc && !thumbnailErrors[asset.assetInstanceId] ? (
                        <Box
                          onClick={() => navigate(buildAssetDetailPath(asset), { state: buildAssetNavState(asset) })}
                          sx={{
                            cursor: 'pointer',
                            '&:hover img': {
                              opacity: 0.85,
                            },
                          }}
                        >
                          <Avatar
                            variant="square"
                            src={thumbnailSrc}
                            onError={() => handleThumbnailError(asset.assetInstanceId)}
                            sx={{ height: thumbnailHeight, width: thumbnailWidth, borderRadius: compactRows ? 2 : 3 }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            height: fallbackHeight,
                            width: fallbackWidth,
                            backgroundColor: '#ffffff',
                            border: '1px solid #ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.85,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.8rem',
                              color: 'black',
                              textAlign: 'center',
                            }}
                          >
                            No Thumbnail
                          </Typography>
                        </Box>
                      )}
                    </Box>


                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Box display="flex" flexDirection="column" justifyContent="space-between" height={actionColumnHeight}>
                        <Tooltip title="Copy Asset ID">
                          <IconButton
                            data-cy={`asset-table-copy-${asset.assetInstanceId}`}
                            component="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigator.clipboard.writeText(asset.assetId);
                            }}
                            sx={{
                              padding: 0.25,            // make button tighter
                              color: (theme) => theme.palette.grey[400], // light grey
                              '&:hover': {
                                color: (theme) => theme.palette.grey[600], // slightly darker on hover
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14 }} /> {/* super small */}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Open in New Tab">
                          <IconButton
                            data-cy={`asset-table-newtab-${asset.assetInstanceId}`}
                            component="a"
                            href={buildAssetDetailPath(asset)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()} // ✅ Prevents row click but allows new tab
                            sx={{
                              padding: 0.25,
                              color: (theme) => theme.palette.grey[400],
                              "&:hover": {
                                color: (theme) => theme.palette.grey[600],
                              },
                            }}
                          >
                            <OpenInNewIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download Images">
                          <IconButton
                            data-cy={`asset-table-download-${asset.assetInstanceId}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadFilesAsync(asset, { systems });
                            }}
                            sx={{
                              padding: 0.25,
                              color: (theme) => theme.palette.grey[400],
                              "&:hover": {
                                color: (theme) => theme.palette.grey[600],
                              },
                            }}
                          >
                            <DownloadIcon fontSize="inherit" sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell sx={{ px: 0 }}>
                  {compactRows ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.75,
                        maxWidth: 260,
                        py: 1,
                      }}
                    >
                      {nonZeroImageCounts.length ? (
                        nonZeroImageCounts.map(({ key, compactLabel, count }) => {
                          const hasMissingCameras = key === 'imageExterior' && missingCameraPositions.length > 0;
                          const chip = (
                            <Box
                              key={key}
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 999,
                                backgroundColor: (theme) => theme.palette.action.hover,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  color: hasMissingCameras ? 'error.main' : 'text.primary',
                                }}
                              >
                                {compactLabel}: {count}
                              </Typography>
                            </Box>
                          );

                          return hasMissingCameras ? (
                            <Tooltip key={key} title={missingCameraTooltip} arrow>
                              {chip}
                            </Tooltip>
                          ) : chip;
                        })
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No images
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    imageCountItems.map(({ key, label, getCount }) => {
                      const hasMissingCameras = key === 'imageExterior' && missingCameraPositions.length > 0;
                      const countValue = (
                        <Typography sx={{ color: hasMissingCameras ? 'error.main' : 'text.primary' }}>
                          {getCount(asset)}
                        </Typography>
                      );

                      return (
                        <Grid2 container key={key} sx={{ gap: 0.5, marginRight: 6 }}>
                          <Grid2 size={{ xs: 12, sm: 10 }} sx={{ alignItems: 'end', p: 0, width: 'fit-content' }}>
                            <Typography sx={{ textAlign: 'end' }}>
                              {`${label}:`}
                            </Typography>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 1 }}>
                            {hasMissingCameras ? (
                              <Tooltip title={missingCameraTooltip} arrow>
                                {countValue}
                              </Tooltip>
                            ) : countValue}
                          </Grid2>
                        </Grid2>
                      );
                    })
                  )}
                </TableCell>

                <TableCell>
                    <AssetStatusTag
                    flagValue={asset.processingProcessFlagValue}
                    title={asset.processingError || undefined}
                  >
                    {asset.processingCurrentStep}
                  </AssetStatusTag>
                </TableCell>
                <TableCell>
                  <AssetStatusTag
                    flagValue={asset.syndicationProcessFlagValue}
                    title={asset.syndicationError || undefined}
                  >
                    {asset.syndicationCurrentStep}
                  </AssetStatusTag>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(+asset.created_at * 1000).toLocaleString([], {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>{configNameByKeyBySystem[asset.systemCode]?.[asset.config] ?? asset.config}</TableCell>
                <TableCell>
                  {asset.imagePano?.length ? (
                    <SpinningButton data-cy={`asset-table-360-${asset.assetInstanceId}`} onClick={(e: React.MouseEvent) => handle360Click(e, asset.systemCode, asset.assetInstanceId)}>
                      360
                      <SyncTwoToneIcon sx={{ fontSize: '18px', marginLeft: '5px' }} />
                    </SpinningButton>
                  ) : (
                    <Typography>No Panos</Typography>
                  )}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Asset 360 Modal */}
      <Asset360Modal
        open={openModal}
        handleClose={handleCloseModal}
        systemCode={modalAsset?.systemCode || null}
        assetInstanceId={modalAsset?.assetInstanceId || null}
      />
    </>
  );
};

export default AssetDesktopTable;
